import { Layout } from '@/Layout';
import { BackLink } from '@/components/BackLink';
import {
  ConfirmModalArgs,
  useConfirmModal,
} from '@/components/ConfirmationModal';
import {
  TransactionItemStatus,
  useDeleteTransactionItem,
} from '@/context/services/transaction_items/TransactionItem.service';
import { Search } from '@/features/transaction_items/Detail/Search/Search';
import { TransactionItemsDetailOptionsTableView } from '@/features/transaction_items/Detail/Table/Table';
import { DetailTopContainer } from '@/features/transaction_items/Detail/Top/Top';
import { TransactionDetailOptionDrawer } from '@/features/transaction_items/Detail/components/Drawer/TransactionDetailOptionDrawer';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useTransactionDetailOptionDrawer } from '@/features/transaction_items/Detail/hooks/useTransactionDetailOptionDrawer';
import {
  useTransactionItemDetailSearchQuery,
  useTransactionItemDetailSearchQueryInitial,
} from '@/features/transaction_items/Detail/hooks/useTransactionItemDetailSearchQuery';
import { useCalculateTableHeight } from '@/features/transaction_items/hooks/useCalculateTableHeight';
import { useGetTableContainerHeight } from '@/features/transaction_items/hooks/useGetTableContainerHeight';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useNavigate } from '@/router';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  Tooltip,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './TransactionItemDetail.module.scss';

const cx = classNames.bind(styles);

export type Props = {};

export const TransactionItemDetail: FC<Props> = memo(({}) => {
  const { report_type_id, transaction_item_id } = useParams();
  const { t } = useTranslation();
  const globalContainer = useGlobalContainerRef();

  const [isDetailTopLoading, setDetailTopLoading] = useState(true);
  const [isDetailTableLoading, setDetailTableLoading] = useState(true);
  const { isEdit } = useTransactionItemDetailEdit();
  const [transactionItemData, setTransactionItemData] = useState<{
    can_delete?: boolean;
    delete_tooltip?: string;
  } | null>(null);

  const isDisabled = isDetailTopLoading || isDetailTableLoading || isEdit;
  const isDeleteDisabled =
    isDisabled ||
    transactionItemData === null ||
    transactionItemData?.can_delete === false;

  const confirmModalArgs: ConfirmModalArgs = useMemo(
    () => ({
      title: t('transaction_items.delete.confirm.title'),
      body: t('transaction_items.delete.confirm.body'),
      okButtonProps: {
        color: 'danger',
        isSecondary: true,
        children: t('delete'),
        testId: 'transaction-item-delete-button',
      },
      cancelButtonProps: {
        isTertiary: true,
        children: t('cancel'),
        testId: 'transaction-item-cancel-button',
      },
    }),
    [t]
  );

  const { confirm, ConfirmModal } = useConfirmModal(confirmModalArgs);

  const navigate = useNavigate();
  const deleteTransactionItem = useDeleteTransactionItem();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();

  const onClickTransactionItemDelete = useCallback(async () => {
    const confirmed = await confirm();
    if (confirmed) {
      try {
        const result = await deleteTransactionItem(transaction_item_id);

        if (result.status === TransactionItemStatus.SUCCESS) {
          if (report_type_id) {
            navigate('/report_types/:report_type_id/transaction_items', {
              params: { report_type_id },
              state: {
                transaction_item_deleted: true,
              },
            });
          }
        } else if (result.status === TransactionItemStatus.AUTH_ERROR) {
          location.href = result.redirectPath;
          return;
        } else if (
          result.status === TransactionItemStatus.ERROR ||
          result.status === TransactionItemStatus.INVALID
        ) {
          notify.error({
            title: t('transaction_items.delete.error'),
            duration: 5,
          });
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        notify.error({
          title: t('transaction_items.delete.error'),
          duration: 5,
        });
      }
    }
  }, [
    confirm,
    navigate,
    report_type_id,
    deleteTransactionItem,
    notify,
    t,
    transaction_item_id,
    send,
  ]);

  const defaultValue = useTransactionItemDetailSearchQueryInitial();
  const { searchQuery, handleSearch } =
    useTransactionItemDetailSearchQuery(defaultValue);

  const containerHeight = useGetTableContainerHeight();
  const divRef = useRef<HTMLDivElement>(null);
  const topDivRef = useRef<HTMLDivElement>(null);
  const tableBodyHeight = useCalculateTableHeight(
    containerHeight - (topDivRef.current?.clientHeight ?? 0),
    divRef
  );

  // Drawer state management
  const drawer = useTransactionDetailOptionDrawer();

  return (
    <div ref={divRef}>
      <Layout
        title={t('transaction_items.detail.title')}
        hideContentPadding
        backLink={
          <BackLink
            href={`/report_types/${report_type_id}/transaction_items`}
            text={t('transaction_items.detail.back')}
          />
        }
        className='transaction-item-layout'
        headerRight={
          <Tooltip
            title={
              transactionItemData?.can_delete === false &&
              transactionItemData?.delete_tooltip
                ? transactionItemData.delete_tooltip
                : undefined
            }
            placement='bottom'
            getPopupContainer={globalContainer}
          >
            <span>
              <ButtonV2
                isDisabled={isDeleteDisabled}
                color='danger'
                isSecondary
                onClick={onClickTransactionItemDelete}
              >
                {t('delete')}
              </ButtonV2>
            </span>
          </Tooltip>
        }
      >
        <div className={cx(styles['container'])}>
          <div ref={topDivRef} className={cx(styles['top-container'])}>
            <DetailTopContainer
              setDetailTopLoading={setDetailTopLoading}
              setTransactionItemData={setTransactionItemData}
            />
            <Search onSearch={handleSearch} isDisabled={isDisabled} />
          </div>
          <TransactionItemsDetailOptionsTableView
            tableBodyHeight={tableBodyHeight}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            setDetailTableLoading={setDetailTableLoading}
            isEdit={isEdit}
            onEdit={drawer.openEditDrawer}
            onAdd={drawer.openAddDrawer}
          />
          <TransactionDetailOptionDrawer
            mode={drawer.mode}
            visible={drawer.isVisible}
            onClose={drawer.closeDrawer}
            onSave={drawer.handleSave}
            methods={drawer.methods}
          />
        </div>
        <ConfirmModal />
      </Layout>
    </div>
  );
});
TransactionItemDetail.displayName = 'TransactionItemDetail';
